import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AIClipHistoryPage, listAIClipTasks } from '../../../api/aiClip';
import AIClipWorkspaceModal, { AIClipWorkspaceSummary } from './AIClipWorkspaceModal';

interface AIClipControlsProps {
  episodeId: string;
  gateReady: boolean;
}

const POLL_INTERVAL_MS = 30_000;

const summaryFrom = (page: AIClipHistoryPage): AIClipWorkspaceSummary => ({
  activeTask: page.activeTask,
  total: page.total ?? page.items.length,
  submissionPending: false,
});

const AIClipControls = ({ episodeId, gateReady }: AIClipControlsProps) => {
  const { t } = useTranslation('episodeDetail');
  const [summary, setSummary] = useState<AIClipWorkspaceSummary | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);
  const [pollNonce, setPollNonce] = useState(0);
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    const page = await listAIClipTasks(episodeId, { limit: 1 });
    const next = summaryFrom(page);
    setSummary(next);
    return next;
  }, [episodeId]);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setIsLoaded(false);
    setHasLoadFailed(false);
    listAIClipTasks(episodeId, { limit: 1 })
      .then(page => {
        if (cancelled) return;
        setSummary(summaryFrom(page));
        setIsLoaded(true);
      })
      .catch(err => {
        if (cancelled) return;
        setIsLoaded(true);
        setHasLoadFailed(true);
        // eslint-disable-next-line no-console
        console.warn(`[AIClipControls] history summary load failed episodeId=${episodeId}`, err);
      });
    return () => { cancelled = true; };
  }, [episodeId, loadNonce]);

  useEffect(() => {
    if ((!summary?.activeTask && !summary?.submissionPending) || isWorkspaceOpen) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        if (!cancelled) await loadSummary();
      } catch (err) {
        if (!cancelled) setPollNonce(value => value + 1);
      }
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isWorkspaceOpen, loadSummary, pollNonce, summary?.activeTask, summary?.submissionPending]);

  if (!isLoaded) return null;

  if (hasLoadFailed) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--danger, #d33)' }}>
          {t('aiClip.statusLoadFailed')}
        </span>
        <button type="button" className="btn btn-secondary" onClick={() => setLoadNonce(value => value + 1)}>
          {t('aiClip.retry')}
        </button>
      </span>
    );
  }

  const total = summary?.total ?? 0;
  const active = summary?.activeTask ?? null;
  let label = t('aiClip.button');
  if (active?.cancelRequestedAt) label = `${t('aiClip.workspace.cancelling')}…`;
  else if (active || summary?.submissionPending) label = t('aiClip.clipping');
  if (total > 0) label = `${label} · ${total}`;

  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setIsWorkspaceOpen(true)}>
        {label}
      </button>
      <AIClipWorkspaceModal
        open={isWorkspaceOpen}
        onClose={() => setIsWorkspaceOpen(false)}
        episodeId={episodeId}
        gateReady={gateReady}
        summary={summary}
        onSummaryChange={setSummary}
      />
    </>
  );
};

export default AIClipControls;
